import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ParamMap } from '@angular/router';
import { Block } from '@app/core/models/block/block.model';
import { GlobalService } from '@app/core/services/common/global.service';
import { FiltersService } from '@app/shared/services/filters/filters.service';
import { ListDetailsService } from '@app/shared/services/list/list-details.service';
import { BlockTypeEnum } from '@core/enums';
import { ViewPart } from '@core/models';
import { SubSink } from '@core/utils/subsink';
import {
  FilterRangeService,
} from '@modules/creator/components/special-block/advanced-filters/components/filter-range/filter-range.service';
import { AdvancedFiltersService } from '@shared/services/filters/advanced-filters.service';
import { map, take } from 'rxjs/operators';

@Component({
  selector: 'app-filter-range',
  templateUrl: './filter-range.component.html',
  styleUrls: ['./filter-range.component.scss'],
  standalone: false,
})
export class FilterRangeComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() item: Block;
  @Input() blockType: number;
  blockTypeEnum = BlockTypeEnum;
  minPlaceholder: string;
  maxPlaceholder: string;
  subSink = new SubSink();

  private static readonly LIVE_COUNTS_DEBOUNCE_MS = 300;
  private liveCountsDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Loading state check
  get isLoading(): boolean {
    return ListDetailsService.isLoading.value;
  }

  typeItem = 0;
  sliderName: string;
  sliderClassName = 'sliderClassName';
  blockParent = 'blockParent';
  blockTitle = 'blockTitle';
  blockSlider = 'blockSlider';
  blockInput = 'blockInput';
  inputBlock = 'inputBlock';
  inputStart = 'inputStart';
  inputline = 'inputline';
  inputEnd = 'inputEnd';
  inputClin = 'inputClin';
  matSlider = 'matSlider';
  blockSliderLineOnOff = false;

  constructor(
    private fs: FiltersService,
    public localfs: FilterRangeService,
    private el: ElementRef,
    private renderer: Renderer2,
    private globalService: GlobalService,
    private cdr: ChangeDetectorRef,
    private advancedFiltersService: AdvancedFiltersService,
  ) {
  }

  ngAfterViewInit(): void {
    // Subscribe to URL parameter changes to sync filter values with current URL state
    this.fs.filtersFrom$.subscribe(params => {
      this.syncFiltersWithUrl(params);
    });

    // Subscribe to data updates from API to update min/max range values
    this.fs.listFilterSlidable.subscribe(data => {
      if (!data || data.length === 0) {
        return;
      }
      this.updateMinMaxValues(data[0]);
    });

    this.typeItem = this.item.type;
    this.setStyle(window.innerWidth, this.item);
  }

  ngOnDestroy(): void {
    this.clearLiveCountsTimer();
    this.subSink.unsubscribe();
  }

  private clearLiveCountsTimer(): void {
    if (this.liveCountsDebounceTimer !== null) {
      clearTimeout(this.liveCountsDebounceTimer);
      this.liveCountsDebounceTimer = null;
    }
  }

  /**
   * Live liczniki przy pisaniu — bez applyFilter / fs.search.
   * Lista odświeża się dopiero na change / Enter / blur.
   */
  private scheduleLiveCountsUpdate(type: number, key: string): void {
    this.clearLiveCountsTimer();
    this.liveCountsDebounceTimer = setTimeout(() => {
      this.liveCountsDebounceTimer = null;
      if (this.isLoading) {
        return;
      }
      if (!this.validInput(type, key)) {
        return;
      }
      this.advancedFiltersService.notifyRangesChanged();
    }, FilterRangeComponent.LIVE_COUNTS_DEBOUNCE_MS);
  }

  ngOnInit(): void {
    this.sliderClassName = 'sliderClassName' + this.item.type;
    this.blockParent = 'blockParent' + this.item.type;
    this.blockTitle = 'blockTitle' + this.item.type;
    this.blockSlider = 'blockSlider' + this.item.type;
    this.blockInput = 'blockInput' + this.item.type;
    this.inputBlock = 'inputBlock' + this.item.type;
    this.inputStart = 'inputStart' + this.item.type;
    this.inputline = 'inputline' + this.item.type;
    this.inputEnd = 'inputEnd' + this.item.type;
    this.matSlider = 'matSlider' + this.item.type;
    this.inputClin = 'inputClin' + this.item.type;

    this.globalService.mediaQuery.subscribe(() => {
      this.setStyle(window.innerWidth, this.item);
    });

    if (this.item && this.item.children.length > 0) {
      this.item.children.forEach(e => {
        if (e?.settings?.viewPart == 'line-range-parent') {
          this.blockSliderLineOnOff = true;
        }
      });
    }
    this.subSink.sink = this.advancedFiltersService.availableFiltersUpdated$.subscribe(updateValue => {
      const {data} = updateValue;
      if (this.item.type === this.blockTypeEnum.RANGE_PRICE_OF_CREATION) {
        this.minPlaceholder = data.priceRanges.min.toString();
        this.maxPlaceholder = data.priceRanges.max.toString();
        // Bounds muszą pochodzić stąd (AdvancedFiltersService — singleton),
        // bo listFilterSlidable żyje w innej instancji FiltersService niż ten panel
        // i nigdy nie zasila localfs.min/max → validInput blokuje applyFilter.
        this.applyRangeBounds(+data.priceRanges.min, +data.priceRanges.max);
      } else if (this.item.type === this.blockTypeEnum.RANGE_YEAR_OF_CREATION) {
        this.minPlaceholder = data.yearRanges.min.toString();
        this.maxPlaceholder = data.yearRanges.max.toString();
        this.applyRangeBounds(+data.yearRanges.min, +data.yearRanges.max);
      }
    });
  }

  /**
   * Ustawia granice zakresu w `FilterRangeService` (singleton) i inicjuje
   * wartości filtrów, jeśli jeszcze nie ustawione.
   */
  private applyRangeBounds(min: number, max: number): void {
    if (Number.isNaN(min) || Number.isNaN(max)) {
      return;
    }
    this.localfs.minValueStart[this.item.type] = min;
    this.localfs.maxValueEnd[this.item.type] = max;
    this.cdr.detectChanges();
  }

  /**
   * Synchronize filter values with URL parameters
   */
  private syncFiltersWithUrl(params: ParamMap): void {
    if (!params) {
      return;
    }
    if (this.item.type === this.blockTypeEnum.RANGE_PRICE_OF_CREATION) {
      const priceFrom = params.get('priceFrom');
      const priceEnd = params.get('priceEnd');

      if (priceFrom || priceEnd) {
        this.localfs.filterStart[this.item.type] = priceFrom ? +priceFrom : null;
        this.localfs.filterEnd[this.item.type] = priceEnd ? +priceEnd : null;
      } else {
        this.localfs.filterStart[this.item.type] = null;
        this.localfs.filterEnd[this.item.type] = null;
      }
      this.cdr.detectChanges();
    }

    if (this.item.type === this.blockTypeEnum.RANGE_YEAR_OF_CREATION) {
      const yearFrom = params.get('yearFrom');
      const yearEnd = params.get('yearEnd');

      if (yearFrom || yearEnd) {
        this.localfs.filterStart[this.item.type] = yearFrom ? +yearFrom : null;
        this.localfs.filterEnd[this.item.type] = yearEnd ? +yearEnd : null;
      } else {
        this.localfs.filterStart[this.item.type] = null;
        this.localfs.filterEnd[this.item.type] = null;
      }
      this.cdr.detectChanges();
    }
  }

  /**
   * Update min/max values from API response
   */
  private updateMinMaxValues(data: any): void {
    if (!data) {
      return;
    }

    if (this.item.type === this.blockTypeEnum.RANGE_PRICE_OF_CREATION) {
      this.applyRangeBounds(+data.minPrice, +data.maxPrice);
    }

    if (this.item.type === this.blockTypeEnum.RANGE_YEAR_OF_CREATION) {
      this.applyRangeBounds(+data.minYearOfCreation, +data.maxYearOfCreation);
    }
  }

  /**
   * Initialize filter values to default (min-max range) if they are null/undefined
   */
  private initializeFilterValues(): void {
    const currentStart = this.localfs.filterStart[this.item.type];
    const currentEnd = this.localfs.filterEnd[this.item.type];
    const minValue = this.localfs.minValueStart[this.item.type];
    const maxValue = this.localfs.maxValueEnd[this.item.type];

    // Only initialize if values are null/undefined and min/max are available
    if ((currentStart === null || currentStart === undefined) &&
      (currentEnd === null || currentEnd === undefined) &&
      minValue !== null && minValue !== undefined && minValue !== 'start' &&
      maxValue !== null && maxValue !== undefined) {

      // If API returned 0,0 (no results), set to a reasonable default range
      if (minValue === 0 && maxValue === 0) {
        if (this.item.type === this.blockTypeEnum.RANGE_PRICE_OF_CREATION) {
          // For price, use a reasonable default range
          this.localfs.filterStart[this.item.type] = 0;
          this.localfs.filterEnd[this.item.type] = 10000;
        } else if (this.item.type === this.blockTypeEnum.RANGE_YEAR_OF_CREATION) {
          // For year, use a reasonable default range
          this.localfs.filterStart[this.item.type] = 1900;
          this.localfs.filterEnd[this.item.type] = new Date().getFullYear();
        }
      } else {
        // Use API values
        this.localfs.filterStart[this.item.type] = +minValue;
        this.localfs.filterEnd[this.item.type] = +maxValue;
      }
    }
  }

  /**
   * Reset filter values to default (min-max range)
   */
  private resetToDefaultValues(): void {
    const minValue = this.localfs.minValueStart[this.item.type];
    const maxValue = this.localfs.maxValueEnd[this.item.type];

    if (minValue !== null && minValue !== undefined && minValue !== 'start' &&
      maxValue !== null && maxValue !== undefined) {

      // If API returned 0,0 (no results), set to a reasonable default range
      if (minValue === 0 && maxValue === 0) {
        if (this.item.type === this.blockTypeEnum.RANGE_PRICE_OF_CREATION) {
          // For price, use a reasonable default range
          this.localfs.filterStart[this.item.type] = 0;
          this.localfs.filterEnd[this.item.type] = 10000;
        } else if (this.item.type === this.blockTypeEnum.RANGE_YEAR_OF_CREATION) {
          // For year, use a reasonable default range
          this.localfs.filterStart[this.item.type] = 1900;
          this.localfs.filterEnd[this.item.type] = new Date().getFullYear();
        }
      } else {
        // Use API values
        this.localfs.filterStart[this.item.type] = +minValue;
        this.localfs.filterEnd[this.item.type] = +maxValue;
      }

    }
  }

  // Nowa metoda do wyodrębnienia liczby z ciągu znaków
  extractNumber(value: string): number {
    return parseInt(value.replace(/\D/g, ''), 10);
  }

  setStyle(innerWidth, item) {
    const applyStyles = (selector, styleObj) => {
      const elements = document.querySelectorAll(selector);
      for (const key in styleObj) {
        elements.forEach(element => {
          this.renderer.setStyle(element, this.camelToKebabCase(key), styleObj[key]);
        });
      }
    };

    const styleMapping = {
      [ViewPart.SLIDER_RANGE_TITLE]: {
        selector: `.sliderClassName${this.typeItem}`,
        process: () => {
          this.sliderName = item.content;
          return item.styles.all.basic;
        },
      },
      [ViewPart.SLIDER_RANGE_LINE]: {
        selector: `.matSlider${this.typeItem}`,
        process: () => {
          const styleLine = this.parseStyle(item, innerWidth);
          const borderBottomWidthNumber = (this.extractNumber(styleLine?.borderBottomWidth) >= 3) ? -1 : 3 - this.extractNumber(styleLine?.borderBottomWidth);
          this.el.nativeElement.style.setProperty(`--lineColor`, styleLine?.borderBottomColor);
          this.el.nativeElement.style.setProperty(`--heightLine`, styleLine?.borderBottomWidth);
          this.el.nativeElement.style.setProperty(`--stylLine`, styleLine?.borderBottomStyle);
          this.el.nativeElement.style.setProperty(`--borderBottomWidthNumber`, `${borderBottomWidthNumber}px`);
          return Object.fromEntries(Object.entries(styleLine).filter(([key]) => !key.includes('border')));
        },
      },
      [ViewPart.SLIDER_RANGE_INPUT_START]: {
        selector: `.inputStart${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
      [ViewPart.SLIDER_RANGE_INPUT_LINE]: {
        selector: `.inputline${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
      [ViewPart.SLIDER_RANGE_INPUT_END]: {
        selector: `.inputEnd${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
      [ViewPart.SLIDER_RANGE_PARENT]: {
        selector: `.blockTitle${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
      [ViewPart.LINE_RANGE_PARENT]: {
        selector: `.blockSlider${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
      [ViewPart.SLIDER_RANGE_INPUT]: {
        selector: `.blockInput${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
    };

    const mapping = styleMapping[item.settings?.viewPart];
    if (mapping) {
      const styles = mapping.process();
      applyStyles(mapping.selector, styles);
    }

    item.children.forEach(e => this.setStyle(window.innerWidth, e));
    this.fs.paramsGlobalClean = false;
  }

  parseStyle(item, innerWidth) {
    const baseStyle = item?.styles?.all?.basic || {};
    let responsiveStyle = {};
    if (innerWidth <= 991 && innerWidth >= 480) {
      responsiveStyle = item?.styles?.maxWidth991Px?.basic || {};
    } else if (innerWidth <= 479) {
      responsiveStyle = item?.styles?.maxWidth479Px?.basic || {};
    }
    const finalStyle = {...baseStyle, ...responsiveStyle};
    return finalStyle;
  }

  /**
   * Widoczność X — synchronicznie.
   * Poprzedni cache + debounce 50ms zwracał `false` w szablonie, a po
   * wyliczeniu `true` nie wołał `detectChanges()` — po ponownym otwarciu
   * panelu X znikało mimo aktywnego zakresu (np. tylko min 1953).
   */
  sliderChange(type: number): boolean {
    if (type !== this.item.type) {
      return false;
    }

    const currentStart = this.localfs.filterStart[type];
    const currentEnd = this.localfs.filterEnd[type];

    // Puste pola = brak aktywnego zakresu → bez przycisku X.
    // (+null === 0) wcześniej fałszywie pokazywało X przy null.
    if (this.isEmptyRangeValue(currentStart) && this.isEmptyRangeValue(currentEnd)) {
      return false;
    }

    // Jednostronny zakres (tylko min lub tylko max) = aktywny filtr.
    if (this.isEmptyRangeValue(currentStart) || this.isEmptyRangeValue(currentEnd)) {
      return true;
    }

    const minValue = this.localfs.minValueStart[type];
    const maxValue = this.localfs.maxValueEnd[type];

    // Obie strony wypełnione, ale brak granic — i tak pokaż X (jest filtr).
    if (
      minValue === null
      || minValue === undefined
      || minValue === 'start'
      || maxValue === null
      || maxValue === undefined
    ) {
      return true;
    }

    const minApiValue = +minValue;
    const maxApiValue = +maxValue;
    return +currentStart !== minApiValue || +currentEnd !== maxApiValue;
  }

  private isEmptyRangeValue(value: unknown): boolean {
    return value === null
      || value === undefined
      || value === ''
      || (typeof value === 'number' && Number.isNaN(value));
  }

  onInputChange(type, key, inputStatAmount, inputEndAmount) {
    this.cdr.detectChanges();
    this.scheduleLiveCountsUpdate(type, key);
  }

  onSliderInput(type, key, inputStatAmount, inputEndAmount) {
    this.cdr.detectChanges();
    this.scheduleLiveCountsUpdate(type, key);
  }

  onClearClick(type) {
    // Sprawdź czy trwa ładowanie
    if (this.isLoading) {
      return;
    }

    this.clearFilter(type);
  }

  onSliderChange(type, key, inputStatAmount, inputEndAmount) {
    this.clearLiveCountsTimer();

    if (this.isLoading) {
      return;
    }

    // Handle clear operation (key === 'all') - removes filter parameters from URL
    if (key === 'all') {
      // Liczniki przeliczamy przed czyszczeniem URL i niezależnie od niego:
      // panel ma własną, niezainicjalizowaną instancję `FiltersService`
      // (`_params` bywa `undefined`), więc `clearFilterFromUrl()` potrafi rzucić
      // i przerwać wszystko, co następuje po nim.
      this.advancedFiltersService.notifyRangesChanged();
      this.clearFilterFromUrl(type);
    } else {
      // Normal filter operation - validate and apply filter
      const isValid = this.validInput(type, key);

      if (isValid) {
        this.applyFilter(type);
      }
    }
  }

  /**
   * Clear filter values — brak aktywnego zakresu = null (placeholder w inputach).
   */
  private clearFilter(type: number): void {
    this.localfs.filterStart[type] = null;
    this.localfs.filterEnd[type] = null;
    this.onSliderChange(type, 'all', this.localfs.filterStart[type], this.localfs.filterEnd[type]);
  }

  /**
   * Clear filter from URL by removing its parameters
   */
  private clearFilterFromUrl(type: number): void {
    this.fs.filtersFrom$.pipe(
      take(1),
      map((params: ParamMap | null) => {
        const paramsCopy = {};
        // `filtersFrom` startuje z `null`, a panel filtrów ma własną instancję
        // `FiltersService` (serwis nie jest singletonem), której nikt nie zasila
        // parametrami z URL. Bez tego strażnika `params.keys` rzuca wyjątkiem,
        // strumień pada i nie wykonuje się nic poniżej — łącznie z przeliczeniem
        // liczników po wyczyszczeniu zakresu.
        if (!params) {
          return paramsCopy;
        }
        params.keys.forEach(k => {
          if (type === this.blockTypeEnum.RANGE_YEAR_OF_CREATION) {
            if (k !== 'yearFrom' && k !== 'yearEnd') {
              paramsCopy[k] = params.get(k);
            }
          } else if (type === this.blockTypeEnum.RANGE_PRICE_OF_CREATION) {
            if (k !== 'priceFrom' && k !== 'priceEnd') {
              paramsCopy[k] = params.get(k);
            }
          }
        });
        return paramsCopy;
      }),
    ).subscribe(filteredParams => {
      // Liczniki są już przeliczone w `onSliderChange` — tutaj tylko czyszczenie URL.
      this.fs.goTo(filteredParams);
    });
  }

  /**
   * Apply filter by sending search parameters
   */
  private applyFilter(type: number): void {
    if (type === this.blockTypeEnum.RANGE_PRICE_OF_CREATION) {
      const priceRange: Record<string, number> = {};
      const start = this.localfs.filterStart[type];
      const end = this.localfs.filterEnd[type];
      if (!this.isEmptyRangeValue(start)) {
        priceRange.priceFrom = +start;
      }
      if (!this.isEmptyRangeValue(end)) {
        priceRange.priceEnd = +end;
      }
      if (Object.keys(priceRange).length) {
        this.fs.search(priceRange);
      }
    }

    if (type === this.blockTypeEnum.RANGE_YEAR_OF_CREATION) {
      const yearRange: Record<string, number> = {};
      const start = this.localfs.filterStart[type];
      const end = this.localfs.filterEnd[type];
      if (!this.isEmptyRangeValue(start)) {
        yearRange.yearFrom = +start;
      }
      if (!this.isEmptyRangeValue(end)) {
        yearRange.yearEnd = +end;
      }
      if (Object.keys(yearRange).length) {
        this.fs.search(yearRange);
      }
    }

    // Zakresy nie przechodzą przez `setFilters`, więc liczniki w panelu filtrów
    // trzeba przeliczyć jawnie — inaczej zostają na wartościach sprzed zmiany.
    this.advancedFiltersService.notifyRangesChanged();
  }

  camelToKebabCase(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  validateAndCorrectInput(type, key) {
    // Puste pole zostaje null — nie „poprawiamy” do min (null < min === true w JS).
    if (key === 'start') {
      const value = this.localfs.filterStart[type];
      if (this.isEmptyRangeValue(value)) {
        this.localfs.filterStart[type] = null;
        this.cdr.detectChanges();
        this.advancedFiltersService.notifyRangesChanged();
        return;
      }
      if (value < this.localfs.minValueStart[type]) {
        this.localfs.filterStart[type] = this.localfs.minValueStart[type];
      } else if (value > this.localfs.maxValueEnd[type]) {
        this.localfs.filterStart[type] = this.localfs.maxValueEnd[type];
      }
    }

    if (key === 'end') {
      const value = this.localfs.filterEnd[type];
      if (this.isEmptyRangeValue(value)) {
        this.localfs.filterEnd[type] = null;
        this.cdr.detectChanges();
        this.advancedFiltersService.notifyRangesChanged();
        return;
      }
      if (value < this.localfs.minValueStart[type]) {
        this.localfs.filterEnd[type] = this.localfs.minValueStart[type];
      } else if (value > this.localfs.maxValueEnd[type]) {
        this.localfs.filterEnd[type] = this.localfs.maxValueEnd[type];
      }
    }

    // Trigger filter update after correction
    this.onSliderChange(type, key, this.localfs.filterStart[type], this.localfs.filterEnd[type]);
  }

  validInput(type, key) {
    if (key === 'start') {
      const value = this.localfs.filterStart[type];
      return value !== null && value !== undefined &&
        this.localfs.minValueStart[type] <= value && value <= this.localfs.maxValueEnd[type];
    }
    if (key === 'end') {
      const value = this.localfs.filterEnd[type];
      return value !== null && value !== undefined &&
        this.localfs.minValueStart[type] <= value && value <= this.localfs.maxValueEnd[type];
    }
    if (key === 'all') {
      // For 'all' key, check if both start and end values are at their default positions
      const startAtDefault = this.localfs.filterStart[type] === this.localfs.minValueStart[type];
      const endAtDefault = this.localfs.filterEnd[type] === this.localfs.maxValueEnd[type];
      return startAtDefault && endAtDefault;
    }
    return false;
  }
}
